import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import HomeHeader from 'components/commons/HomeHeader';
import EstablishmentCardV2 from 'modules/establishment/card/EstablishmentCardV2';
import EventCardSkeleton from 'components/commons/skeletons/EventCardSkeleton';
import establishmentsApi from 'api/requests/establishments';
import { useEstablishmentFilter } from 'context/establishments/EstablishmentFilter';
import { useCurrentLocation } from 'context/user/CurrentLocation';
import { useUserLocale } from 'context/user/UserLocale';
import { useShowLocationModal } from 'context/general/ShowLocationModal';
import ModalLocation from 'modules/home/ModalLocation';
import paths from 'routes/paths';
import { ChevronDown, X, Store, ArrowUp, Search, SlidersHorizontal } from 'lucide-react';
import EstablishmentFiltersModal from 'modules/establishment/filters/EstablishmentFiltersModal';
import { useSearchSession } from 'helpers/searchLog';

const PER_PAGE = 12;

function citySlugToLabel(slug) {
    if (!slug) return null;
    const parts = slug.split('-');
    // Remove state abbreviation (last 2-char part) if present
    const hasState = parts.length > 1 && parts[parts.length - 1].length === 2;
    const cityParts = hasState ? parts.slice(0, -1) : parts;
    return cityParts.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function cityToSlug(city, state) {
    if (!city) return null;
    return [city, state].filter(Boolean).join('-').toLowerCase()
        .replace(/\s+/g, '-')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export default function UnifiedEstablishmentsSearchPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { establishmentFilter, setEstablishmentFilter } = useEstablishmentFilter();
    const { currentLocation } = useCurrentLocation();
    const { userLocale } = useUserLocale();
    const { setShowLocationModal } = useShowLocationModal();

    const searchTerm = searchParams.get('search') || '';
    const tagsParam = searchParams.get('tags') || '';
    const cityParam = searchParams.get('city') || '';

    const [establishments, setEstablishments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [resolvedCity, setResolvedCity] = useState(null);
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);
    const [mobileSearchValue, setMobileSearchValue] = useState(searchTerm);
    const searchSession = useSearchSession();

    const fetchNextPageRef = useRef(null);
    const observerRef = useRef(null);

    const sentinelRef = useCallback((node) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        if (node) {
            const observer = new IntersectionObserver(
                ([entry]) => { if (entry.isIntersecting) fetchNextPageRef.current?.(); },
                { threshold: 0.1 }
            );
            observer.observe(node);
            observerRef.current = observer;
        }
    }, []);

    // Resolve default city: URL param > GPS locale > localStorage currentLocation > null
    useEffect(() => {
        if (cityParam) {
            setResolvedCity({ slug: cityParam, label: citySlugToLabel(cityParam) });
        } else if (userLocale?.city) {
            const slug = cityToSlug(userLocale.city, userLocale.state);
            setResolvedCity({ slug, label: userLocale.city });
        } else if (currentLocation?.city) {
            const slug = cityToSlug(currentLocation.city, currentLocation.state);
            setResolvedCity({ slug, label: currentLocation.city });
        } else {
            setResolvedCity(null);
        }
    }, [cityParam, userLocale?.city, currentLocation?.city]);

    // When city resolved from GPS/localStorage, sync to URL
    useEffect(() => {
        if (!resolvedCity) return;
        if (cityParam === resolvedCity.slug) return;
        setSearchParams(prev => {
            const p = new URLSearchParams(prev);
            p.set('city', resolvedCity.slug);
            return p;
        }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvedCity?.slug]);

    useEffect(() => {
        const onScroll = () => setShowScrollTop(window.scrollY > 400);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Clear establishmentFilter on unmount
    useEffect(() => () => setEstablishmentFilter(null), []);

    const isLastPage = currentPage >= lastPage;

    function buildPayload(page = 1) {
        const payload = { page, limit: PER_PAGE };
        if (searchTerm) payload.term = searchTerm;

        if (cityParam) {
            const parts = cityParam.split('-');
            const lastPart = parts[parts.length - 1];
            const hasState = parts.length > 1 && lastPart.length === 2 && /^[a-z]+$/.test(lastPart);
            if (hasState) {
                payload.city = parts.slice(0, -1).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                payload.state = lastPart.toUpperCase();
            } else {
                payload.city = parts.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
            }
        }

        const tagsFromFilter = establishmentFilter?.tags?.length
            ? establishmentFilter.tags.join(',') : '';
        const allTags = [tagsParam, tagsFromFilter].filter(Boolean).join(',');
        if (allTags) payload.tags = allTags;
        if (establishmentFilter?.features?.length) payload.features = establishmentFilter.features.join(',');
        if (establishmentFilter?.dates) payload.filter_date = establishmentFilter.dates;
        if (establishmentFilter?.distance_range) payload.distance_range = establishmentFilter.distance_range;
        return payload;
    }

    useEffect(() => {
        let cancelled = false;
        setCurrentPage(1);
        setEstablishments([]);
        setLoading(true);

        // Só a primeira página entra no registro de buscas: as seguintes são a
        // mesma busca rolando, não outra.
        const payload = { ...buildPayload(1), search_session: searchSession.sessionFor() };
        establishmentsApi.filter(payload).then(({ data }) => {
            if (cancelled) return;
            const newEstablishments = data?.data || [];
            setEstablishments(newEstablishments);
            setCurrentPage(data?.meta?.current_page || 1);
            setLastPage(data?.meta?.last_page || 1);
            setTotal(data?.meta?.total || newEstablishments.length);
            setLoading(false);
            setLoadingMore(false);
        }).catch(() => {
            if (cancelled) return;
            setLoading(false);
            setLoadingMore(false);
        });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, tagsParam, cityParam, JSON.stringify(establishmentFilter)]);

    async function fetchPage(page, append = false) {
        if (page === 1) setLoading(true);
        else setLoadingMore(true);
        try {
            const { data } = await establishmentsApi.filter(buildPayload(page));
            const newEstablishments = data?.data || [];
            setEstablishments(prev => append ? [...prev, ...newEstablishments] : newEstablishments);
            setCurrentPage(data?.meta?.current_page || page);
            setLastPage(data?.meta?.last_page || 1);
            setTotal(data?.meta?.total || newEstablishments.length);
        } catch (error) {
            console.error('Error fetching search results:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }

    const fetchNextPage = useCallback(() => {
        if (loadingMore || isLastPage) return;
        fetchPage(currentPage + 1, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingMore, isLastPage, currentPage]);

    fetchNextPageRef.current = fetchNextPage;

    const hasActiveFilters = !!(
        establishmentFilter?.dates || establishmentFilter?.distance_range ||
        establishmentFilter?.tags?.length || establishmentFilter?.features?.length
    );

    const activeFilterCount = (establishmentFilter?.tags?.length || 0) +
        (establishmentFilter?.features?.length || 0) +
        (establishmentFilter?.dates ? 1 : 0) +
        (establishmentFilter?.distance_range ? 1 : 0);

    const activePills = [
        tagsParam && {
            label: tagsParam,
            onRemove: () => {
                const p = new URLSearchParams(searchParams);
                p.delete('tags');
                navigate(`${paths.establishments.search}?${p.toString()}`);
            }
        },
        establishmentFilter?.tags?.length && {
            label: establishmentFilter.tags.join(', '),
            onRemove: () => setEstablishmentFilter({ ...establishmentFilter, tags: [] })
        },
        establishmentFilter?.features?.length && {
            label: `${establishmentFilter.features.length} característica(s)`,
            onRemove: () => setEstablishmentFilter({ ...establishmentFilter, features: [] })
        },
        establishmentFilter?.dates && {
            label: 'Data filtrada',
            onRemove: () => setEstablishmentFilter({ ...establishmentFilter, dates: null })
        },
    ].filter(Boolean);

    const cityLabel = resolvedCity?.label || null;
    const titleLine = cityLabel ? `Estabelecimentos em ${cityLabel}` : 'Todos os estabelecimentos';

    return (
        <PageBackground>
            <HomeHeader />
            <PageWrapper>
                <ListingHeader>
                    <TitleRow>
                        <CityTitle onClick={() => setShowLocationModal(true)}>
                            {titleLine}
                            <ChevronDown size={20} color={colors.occasionalPurple} />
                        </CityTitle>
                        <ResultCount>{loading ? '...' : `${total} estabelecimentos`}</ResultCount>
                    </TitleRow>

                    <MobileSearchRow>
                        <MobileSearchInput>
                            <Search size={16} color={colors.occasionalPurple} />
                            <input
                                type='text'
                                placeholder='Buscar estabelecimentos...'
                                value={mobileSearchValue}
                                onChange={e => setMobileSearchValue(e.target.value)}
                                onKeyUp={e => {
                                    if (e.key === 'Enter') {
                                        const p = new URLSearchParams(searchParams);
                                        if (mobileSearchValue.trim()) p.set('search', mobileSearchValue.trim());
                                        else p.delete('search');
                                        navigate(`${paths.establishments.search}?${p.toString()}`);
                                    }
                                }}
                            />
                            {mobileSearchValue && (
                                <button onClick={() => {
                                    setMobileSearchValue('');
                                    const p = new URLSearchParams(searchParams);
                                    p.delete('search');
                                    navigate(`${paths.establishments.search}?${p.toString()}`);
                                }}>
                                    <X size={14} color={colors.nightLight} />
                                </button>
                            )}
                        </MobileSearchInput>
                        <MobileFilterButton $active={activeFilterCount > 0} onClick={() => setIsFiltersOpen(true)}>
                            <SlidersHorizontal size={16} color={activeFilterCount > 0 ? colors.neutralLight : colors.occasionalPurple} />
                            {activeFilterCount > 0 && <MobileFilterBadge>{activeFilterCount}</MobileFilterBadge>}
                        </MobileFilterButton>
                    </MobileSearchRow>

                    {activePills.length > 0 && (
                        <PillsRow>
                            {activePills.map((pill, i) => (
                                <FilterPill key={i}>
                                    {pill.label}
                                    <button onClick={pill.onRemove}><X size={12} /></button>
                                </FilterPill>
                            ))}
                            <ClearAllLink onClick={() => {
                                setEstablishmentFilter(null);
                                const p = new URLSearchParams(searchParams);
                                p.delete('tags');
                                p.delete('search');
                                navigate(`${paths.establishments.search}?${p.toString()}`);
                            }}>
                                Limpar tudo
                            </ClearAllLink>
                        </PillsRow>
                    )}
                </ListingHeader>

                <ResultsArea>
                    {loading ? (
                        <CardsGrid>
                            {[...Array(8)].map((_, i) => <EventCardSkeleton key={i} />)}
                        </CardsGrid>
                    ) : establishments.length === 0 ? (
                        <EmptyState>
                            <Store size={48} color={colors.nightLight} />
                            <EmptyTitle>Nenhum estabelecimento encontrado</EmptyTitle>
                            <EmptySubtitle>Tente remover um filtro ou explore outra cidade</EmptySubtitle>
                            {(hasActiveFilters || tagsParam || searchTerm) && (
                                <ClearFiltersBtn onClick={() => {
                                    setEstablishmentFilter(null);
                                    navigate(paths.establishments.search + (cityParam ? `?city=${cityParam}` : ''));
                                }}>
                                    Limpar filtros
                                </ClearFiltersBtn>
                            )}
                        </EmptyState>
                    ) : (
                        <>
                            <CardsGrid>
                                {establishments.map((establishment, i) => (
                                    <AnimatedCard key={establishment.slug} $index={i}>
                                        <EstablishmentCardV2
                                            data={establishment}
                                            onCardClick={() => {
                                                searchSession.logClick('establishment', establishment, i);
                                                navigate(`/estabelecimento/${establishment.slug}/detalhes`);
                                            }}
                                        />
                                    </AnimatedCard>
                                ))}
                                {loadingMore && [...Array(4)].map((_, i) => (
                                    <EventCardSkeleton key={`sk-${i}`} />
                                ))}
                            </CardsGrid>
                            {!isLastPage && <Sentinel ref={sentinelRef} />}
                            {isLastPage && <EndMessage>Você viu todos os resultados 🎉</EndMessage>}
                        </>
                    )}
                </ResultsArea>
            </PageWrapper>

            <ModalLocation />

            <EstablishmentFiltersModal isOpen={isFiltersOpen} onClose={() => setIsFiltersOpen(false)} />

            {showScrollTop && (
                <ScrollTopButton onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    <ArrowUp size={18} />
                </ScrollTopButton>
            )}
        </PageBackground>
    );
}

/* ===== Animations ===== */
const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
`;

/* ===== Layout ===== */
const PageBackground = styled.div`
    min-height: 100vh;
    background: ${colors.neutralBackground};
`;

const PageWrapper = styled.div`
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 16px 60px;

    @media screen and (min-width: ${breakpoints.lg}) {
        padding: 0 40px 60px;
    }
`;

const ListingHeader = styled.div`
    padding-top: 16px;
    margin-bottom: 28px;

    @media screen and (min-width: ${breakpoints.lg}) {
        padding-top: 120px;
    }
`;

/* ===== Title row ===== */
const TitleRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const CityTitle = styled.h1`
    font-size: 24px;
    font-weight: 600;
    color: ${colors.nightDark};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    transition: opacity 0.15s ease;

    &:hover { opacity: 0.75; }

    @media screen and (min-width: ${breakpoints.lg}) {
        font-size: 28px;
    }
`;

const ResultCount = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
    font-weight: 500;
    white-space: nowrap;
`;

const PillsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    flex-wrap: wrap;
`;

const FilterPill = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    background: ${colors.occasionalPurple}18;
    border: 1px solid ${colors.occasionalPurple}40;
    border-radius: 50px;
    font-size: 12px;
    font-weight: 500;
    color: ${colors.occasionalPurple};

    button {
        background: none;
        border: none;
        cursor: pointer;
        padding: 0;
        display: flex;
        align-items: center;
        color: ${colors.occasionalPurple};
    }
`;

const ClearAllLink = styled.span`
    font-size: 12px;
    color: ${colors.night};
    cursor: pointer;
    text-decoration: underline;

    &:hover { color: ${colors.nightDark}; }
`;

/* ===== Results area ===== */
const ResultsArea = styled.div``;

/* ===== Cards grid ===== */
const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(4, minmax(0, 315px));
        justify-content: space-between;
        gap: 20px;
    }
`;

const AnimatedCard = styled.div`
    min-width: 0;
    animation: ${fadeInUp} 0.35s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: ${({ $index }) => Math.min($index * 30, 200)}ms;
`;

/* ===== Infinite scroll sentinel ===== */
const Sentinel = styled.div`
    height: 60px;
    width: 100%;
`;

/* ===== End message ===== */
const EndMessage = styled.p`
    text-align: center;
    font-size: 14px;
    color: ${colors.nightLight};
    margin-top: 32px;
    padding: 16px;
`;

/* ===== Empty state ===== */
const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 80px 24px;
    gap: 8px;
`;

const EmptyTitle = styled.p`
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 8px 0 4px;
`;

const EmptySubtitle = styled.span`
    font-size: 14px;
    color: ${colors.night};
`;

const ClearFiltersBtn = styled.button`
    margin-top: 16px;
    padding: 10px 24px;
    border-radius: 50px;
    border: 1px solid ${colors.occasionalPurple}60;
    background: transparent;
    color: ${colors.occasionalPurple};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.occasionalPurple}10;
        border-color: ${colors.occasionalPurple};
    }
`;

/* ===== Scroll to top ===== */
const ScrollTopButton = styled.button`
    position: fixed;
    bottom: 28px;
    right: 28px;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: ${colors.nightDark};
    border: none;
    color: ${colors.neutralLight};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
    z-index: 100;

    &:hover {
        background: ${colors.occasionalPurple};
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
    }
`;

/* ===== Mobile search bar ===== */
const MobileSearchRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 14px;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

const MobileSearchInput = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: ${colors.occasionalPurple}14;
    border: 1px solid ${colors.occasionalPurple}40;
    border-radius: 10px;

    input {
        flex: 1;
        border: none;
        background: transparent;
        font-size: 14px;
        color: ${colors.nightDark};
        outline: none;
        font-family: inherit;
        &::placeholder { color: ${colors.occasionalPurple}99; }
    }

    button {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        display: flex;
        align-items: center;
    }
`;

const MobileFilterButton = styled.button`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    border-radius: 10px;
    border: 1px solid ${({ $active }) => $active ? colors.occasionalPurple : `${colors.occasionalPurple}60`};
    background: ${({ $active }) => $active ? colors.occasionalPurple : 'transparent'};
    cursor: pointer;
    transition: all 0.2s ease;
`;

const MobileFilterBadge = styled.div`
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    border-radius: 9px;
    background: ${colors.occasionalPurple};
    border: 2px solid ${colors.neutralLight};
    color: ${colors.neutralLight};
    font-size: 10px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
`;
